import { useState } from 'react';
import classNames from 'classnames';
import Image from 'next/image';
import { Banknote, Info, RefreshCw, ShieldCheck, Truck } from 'lucide-react';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import { isPurchaseFlowEnabled } from '@/lib/purchase-flow';
import { getTradeInRange, isTradeInAvailable } from '@/lib/trade-in';
import type { TradeInQuoteT } from '@/lib/trade-in-quote';
import { priceFormater } from '@/settings';
import i18n from '@/settings/i18n';
import TradeInModal, { type TradeInViewT } from './TradeInModal';
import useTradeInQuote from './useTradeInQuote';

const t = i18n.init('buyback');

const USPS = [
  { label: 'Ship for Free', Icon: Truck },
  { label: 'Data Wiped', Icon: ShieldCheck },
  { label: 'Get Paid Fast', Icon: Banknote },
] as const;

const OPTION =
  'flex h-14 flex-col items-center justify-center rounded border px-2 text-xs leading-tight transition-colors focus:outline-none';

// "$470" for a whole amount, "$470.50" otherwise.
const formatValue = (value: number) =>
  priceFormater.short(value, !Number.isInteger(value));

const RANGE = getTradeInRange();

const NoTradeIn = ({ onAdd }: { onAdd: () => void }) => (
  <div className="mt-2 grid grid-cols-2 gap-3 px-2">
    <button
      type="button"
      onClick={onAdd}
      className={classNames(OPTION, 'border-gray-700/25 hover:border-gray-700')}
    >
      <span>{t`Add a trade-in`}</span>
      {RANGE && (
        <span className="mt-1 text-xxs text-gray-700/60">
          {t`Get`}{' '}
          <span className="font-bold text-[#128452]" translate="no">
            {RANGE}
          </span>
        </span>
      )}
    </button>
    {/* Selected until a trade-in is added. */}
    <button
      type="button"
      aria-pressed
      className={classNames(OPTION, 'cursor-default border-gray-700')}
    >
      {t`No trade-in`}
    </button>
  </div>
);

/**
 * The trade-ins added so far: the latest one's image, and its name (one
 * device) or "N devices" over every name (several), at their combined value.
 */
const TradeIns = ({
  quotes,
  onEdit,
}: {
  /** Oldest first, as stored; never empty. */
  quotes: TradeInQuoteT[];
  onEdit: () => void;
}) => {
  const quote = quotes[quotes.length - 1];
  const value = quotes.reduce((sum, { value: v }) => sum + v, 0);

  return (
    <div className="mt-2 px-2">
      <div className="flex items-center gap-3 rounded border border-gray-700 px-3 py-2 text-xs">
        {quote.thumbnail ? (
          <Image
            src={quote.thumbnail}
            alt=""
            aria-hidden="true"
            width={40}
            height={40}
            className="h-10 w-10 shrink-0 object-contain"
            unoptimized
          />
        ) : (
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-700 text-white">
            <RefreshCw className="h-4 w-4" strokeWidth={2.5} />
          </span>
        )}
        {quotes.length > 1 ? (
          <span className="min-w-0 flex-1">
            <span className="block font-bold">
              {t`${{ count: String(quotes.length) }} devices`}
            </span>
            <span className="mt-0.5 block truncate text-gray-700/60">
              {[...quotes]
                .reverse()
                .map(({ name }) => name)
                .join(', ')}
            </span>
          </span>
        ) : (
          <span className="min-w-0 flex-1 truncate font-bold">
            {quote.name}
          </span>
        )}
        <span className="shrink-0 text-right" translate="no">
          <span className="text-gray-700/60">{t`Trade-in value`}</span>{' '}
          <span className="font-bold text-[#128452]">{formatValue(value)}</span>
        </span>
        <button
          type="button"
          onClick={onEdit}
          className="shrink-0 font-medium underline hover:text-gray-700/80"
        >
          {t`Edit`}
        </button>
      </div>
    </div>
  );
};

/**
 * The PDP's trade-in choice, right below the variants, in the purchase flow
 * only. "No trade-in" until the widget hands back a quote; then the latest
 * trade-in, whose Edit opens all of them.
 */
const TradeInOption = () => {
  const { customer } = useCustomerContext();
  const { quotes, remove } = useTradeInQuote();
  const [view, setView] = useState<TradeInViewT | null>(null);

  if (!isTradeInAvailable() || !isPurchaseFlowEnabled(customer?.email)) {
    return null;
  }

  return (
    <div className="mt-4" id="e2e-pdp-trade-in">
      <div className="flex items-center gap-x-1 px-2">
        <h6 className="text-xxs font-medium uppercase">{t`Trade in`}</h6>
        {/* As the Condition variant's info button. */}
        <button
          type="button"
          aria-label={t`How Trade-in works`}
          className="shrink-0 focus:outline-none"
          onClick={() => setView('info')}
        >
          <Info className="-mt-0.5 h-3 w-3" />
        </button>
      </div>
      {quotes.length > 0 ? (
        <TradeIns quotes={quotes} onEdit={() => setView('offer')} />
      ) : (
        <NoTradeIn onAdd={() => setView('widget')} />
      )}
      <ul className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 px-2 text-xxs">
        {USPS.map(({ label, Icon }) => (
          <li key={label} className="flex items-center gap-1">
            <Icon className="h-3 w-3 shrink-0" />
            {t(label)}
          </li>
        ))}
      </ul>
      <TradeInModal
        view={view}
        setView={setView}
        quotes={quotes}
        onRemove={remove}
      />
    </div>
  );
};

export default TradeInOption;
